import React from 'react'
import { Table, Card, Button, notification } from 'antd'
import { DownloadOutlined, CopyOutlined } from '@ant-design/icons'
import { QRCodeData, QrCodeFormData, QrCodeState } from 'Types/QrCode.d'

interface QrCodeResultsTableProps {
  formData: QrCodeFormData
  state: QrCodeState
  qrCodeContainerRef: React.RefObject<HTMLDivElement>
  onDownloadQRCodes: () => void
  onPreview: (image: string, title: string) => void
}

const QrCodeResultsTable: React.FC<QrCodeResultsTableProps> = ({
  formData,
  state,
  qrCodeContainerRef,
  onDownloadQRCodes,
  onPreview
}) => {
  if (!state.qrCodeData.length) return null

  const qrCodeColumns = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      ellipsis: true,
    },
    {
      title: 'URL with UTM',
      dataIndex: 'product_url',
      key: 'product_url',
      ellipsis: true,
      render: (text: string) => (
        <div className="flex">
          <span className="truncate mr-2">{text}</span>
          <Button 
            icon={<CopyOutlined />} 
            size="small"
            onClick={() => {
              navigator.clipboard.writeText(text)
              notification.success({
                message: 'URL copied',
                description: 'Product URL has been copied to clipboard',
                duration: 2
              })
            }}
          />
        </div>
      ),
    },
    {
      title: 'QR Code (Black on Transparent)',
      dataIndex: 'qr_code_white',
      key: 'qr_code_white',
      render: (dataUrl: string, record: QRCodeData) => dataUrl ? (
        <div className="flex justify-center">
          <div className="checker-bg p-1 rounded">
            <img 
              src={dataUrl} 
              width={100} 
              alt="QR Code (Black on Transparent)" 
              className="cursor-pointer hover:opacity-80" 
              onClick={() => onPreview(dataUrl, `${record.sku} - QR Code (Black on Transparent)`)}
            />
          </div>
        </div>
      ) : null,
    },
    {
      title: 'QR Code (White on Transparent)',
      dataIndex: 'qr_code_black',
      key: 'qr_code_black',
      render: (dataUrl: string, record: QRCodeData) => dataUrl ? (
        <div className="flex justify-center">
          <div className="checker-bg-dark p-1 rounded">
            <img 
              src={dataUrl} 
              width={100} 
              alt="QR Code (White on Transparent)" 
              className="cursor-pointer hover:opacity-80" 
              onClick={() => onPreview(dataUrl, `${record.sku} - QR Code (White on Transparent)`)}
            />
          </div>
        </div>
      ) : null,
    },
  ]

  return (
    <Card 
      title={<span className="text-lg font-semibold">Generated QR Codes (Print Quality: {formData.qrWidth}x{formData.qrWidth}px)</span>} 
      bordered={false} 
      className="mt-6 shadow-md"
      extra={
        <div className="flex space-x-2">
          <Button 
            onClick={onDownloadQRCodes} 
            icon={<DownloadOutlined />}
            type="primary"
            className="bg-blue-500 hover:bg-blue-600"
            size="middle"
          >
            Download Print-Quality QR Codes ({formData.qrWidth}x{formData.qrWidth}px)
          </Button>
        </div>
      }
    >
      <Table
        columns={qrCodeColumns}
        dataSource={state.qrCodeData}
        scroll={{ x: true }}
        pagination={{ pageSize: 5 }}
        size="middle"
        bordered
        className="print-friendly-table"
      />
      
      {/* Hidden div for printing */}
      <div className="hidden">
        <div ref={qrCodeContainerRef} className="p-4">
          <h2 className="text-xl font-bold mb-4">Print-Quality QR Codes</h2>
          <div className="grid grid-cols-3 gap-4">
            {state.qrCodeData.map((item) => (
              <div key={item.key} className="border p-4 flex flex-col items-center">
                <p className="font-bold">{item.sku}</p>
                <p className="text-sm mb-2">{item.product_name}</p>
                <img src={item.qr_code_white} width={150} alt="QR Code" />
                <p className="text-xs mt-2 text-center break-all">{item.product_url}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  )
}

export default QrCodeResultsTable 